import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ShoppingCart, Phone } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  const navLinks = [
    { href: '/products/commercial', label: 'Commercial' },
    { href: '/products/residential', label: 'Residential' },
    { href: '/products/shop', label: 'Shop Shutters' },
    { href: '/blog', label: 'Blog' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-slate-900 text-white shadow-lg" data-testid="navbar">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            {/* Shield Icon */}
            <svg className="w-10 h-10 lg:w-12 lg:h-12" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M50 5 L90 15 L90 55 C90 78 68 95 50 100 C32 95 10 78 10 55 L10 15 Z" 
                    fill="#0F172A" stroke="#F59E0B" strokeWidth="3"/>
              <line x1="22" y1="30" x2="78" y2="30" stroke="#F59E0B" strokeWidth="5" strokeLinecap="round"/>
              <line x1="22" y1="45" x2="78" y2="45" stroke="#F59E0B" strokeWidth="5" strokeLinecap="round"/>
              <line x1="24" y1="60" x2="76" y2="60" stroke="#F59E0B" strokeWidth="5" strokeLinecap="round"/>
              <line x1="28" y1="75" x2="72" y2="75" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round"/>
              <line x1="36" y1="88" x2="64" y2="88" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            <div className="flex flex-col">
              <span className="font-heading text-xl lg:text-2xl font-bold tracking-wider text-white">
                EMPIRE ROLLERZ
              </span>
              <span className="text-[10px] lg:text-xs text-amber-500 tracking-widest uppercase">
                Security Solutions
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-slate-300 hover:text-amber-500 font-medium transition-colors duration-200"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="tel:02084960499" 
              className="flex items-center space-x-2 text-slate-300 hover:text-amber-500 transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-mono text-sm">020 8496 0499</span>
            </a>
            
            <Link to="/cart" data-testid="cart-link">
              <Button 
                variant="outline" 
                className="relative border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-amber-500 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden flex items-center space-x-4">
            <Link to="/cart" className="relative" data-testid="mobile-cart-link">
              <ShoppingCart className="w-6 h-6 text-white" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-amber-500 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white" data-testid="mobile-menu-btn">
                  {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-slate-900 text-white border-slate-700 w-[300px]">
                <div className="flex flex-col space-y-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className="text-lg font-medium text-slate-300 hover:text-amber-500 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="pt-6 border-t border-slate-700">
                    <a 
                      href="tel:02084960499" 
                      className="flex items-center space-x-2 text-amber-500"
                    >
                      <Phone className="w-5 h-5" />
                      <span className="font-mono">020 8496 0499</span>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
